import { create } from "zustand";
import type {
  CollectionKey,
  DiffAction,
  ImportFieldOverride,
  ImportResolution,
  MergeSide,
  PreviewData,
  FieldDiff,
  ResolutionChoice,
  RowDiff,
} from "@/components/save/profile-merge-types";
import { uniquifyRowKeys } from "@/components/save/profile-merge-row-keys";
import { dateFieldError, isBeforeBirthDate, isStartAfterEnd, todayIso } from "@/lib/date-instant";
import { importFieldError, ROW_FIELD_MAX_CHARS, rowFieldError } from "@/lib/import-field-validation";
import { isKnownLanguageTag } from "@/lib/language-options";

// The person_detail field whose value is a full date (validated + capped like
// the collection MONTH fields). Kept as a set for symmetry with future date keys.
const DATE_DETAIL_KEYS = new Set(["dateOfBirth"]);

const COLLECTION_KEYS: CollectionKey[] = [
  "experiences",
  "educations",
  "languageSkills",
  "certificates",
  "expertises",
];

// === Guided review (the "active guide") ============================
//
// The in-line review walks the user through the actionable items one at a
// time: exactly one merge card is the ACTIVE STEP, showing the decision
// buttons; deciding advances to the next unreviewed step. The walk order
// mirrors the on-page order of the merge cards, NOT the preview payload
// order, so the guide moves down the page instead of jumping around.

/** One stop of the guided review. `key` identifies the card (a person_detail
 *  field key, a collection rowKey, or the synthetic skills-group key); `keys`
 *  are the resolution keys the step decides — one for a field/row, every
 *  actionable skill rowKey for the grouped skills step. */
export interface GuideStep {
  key: string;
  keys: string[];
  action: DiffAction;
}

/** The skills section decides all incoming skills at once (one card, one
 *  choice — see ProfileMergeSkills), so the guide gives it ONE step under
 *  this synthetic key. */
export const SKILLS_STEP_KEY = "__skills__";

// On-page order of the person_detail merge cards (ProfilePage: business card →
// summary → details, personal tab then company tab). Doubles as the set of
// keys that HAVE a card — a key an import sends that no field renders gets no
// step (it would strand the guide on an invisible card); it still counts as a
// change, follows its default/bulk resolution, and commits fine. LinkedIn's
// preview only ever carries the original nine; the rest are the additional
// CV-extractor fields (each wired to a card on ProfilePage).
export const DETAIL_KEY_ORDER = [
  // business card
  "phone",
  "email",
  "country",
  "city",
  // summary
  "bio",
  // details · personal tab
  "gender",
  "dateOfBirth",
  "nationality",
  "maritalStatus",
  "childCount",
  "website",
  "profession",
  "street",
  "houseNumber",
  "apartment",
  "zipCode",
  "drivingLicence",
  "citizenship",
  // details · company tab
  "jobTitle",
  "currentEmployer",
  "currentActivity",
];

/** Which Details-box tab a person_detail merge card lives in. The profile page
 *  watches the active step and switches the tab so the guide never points at
 *  an unmounted card. Keys outside the Details box (phone, email, country, city
 *  on the business card; bio in the summary) are absent. */
export const DETAIL_KEY_TAB: Record<string, "personal" | "company"> = {
  gender: "personal",
  dateOfBirth: "personal",
  nationality: "personal",
  maritalStatus: "personal",
  childCount: "personal",
  website: "personal",
  profession: "personal",
  street: "personal",
  houseNumber: "personal",
  apartment: "personal",
  zipCode: "personal",
  drivingLicence: "personal",
  citizenship: "personal",
  jobTitle: "company",
  currentEmployer: "company",
  currentActivity: "company",
};

// Collection sections in on-page order (left column top-to-bottom, then the
// right column), matching how the cards read on the profile page.
const SECTION_STEP_ORDER: CollectionKey[] = [
  "languageSkills",
  "experiences",
  "educations",
  "expertises",
  "certificates",
];

/** The guided review's steps in on-page order. Empty when there is no preview
 *  or nothing actionable. */
export function guideSteps(preview: PreviewData | null): GuideStep[] {
  if (!preview) return [];
  const steps: GuideStep[] = [];
  const detail = preview.personDetail.filter((d) => d.action !== "UNCHANGED");
  for (const key of DETAIL_KEY_ORDER) {
    const diff = detail.find((d) => d.key === key);
    if (diff) steps.push({ key: diff.key, keys: [diff.key], action: diff.action });
  }
  for (const section of SECTION_STEP_ORDER) {
    const rows = preview[section].filter((r) => r.action !== "UNCHANGED");
    if (rows.length === 0) continue;
    if (section === "expertises") {
      steps.push({ key: SKILLS_STEP_KEY, keys: rows.map((r) => r.rowKey), action: "ADD" });
    } else {
      for (const row of rows) steps.push({ key: row.rowKey, keys: [row.rowKey], action: row.action });
    }
  }
  return steps;
}

/** True when the user has explicitly resolved every key of the step (defaults
 *  don't count — the guide exists to make each default a conscious choice).
 *  Own-property check only: step keys are server-derived, so a genesis field
 *  key or rowKey colliding with an Object.prototype member (`toString`, …)
 *  must not read as decided. */
export function stepDecided(step: GuideStep, choices: Record<string, ResolutionChoice>): boolean {
  return step.keys.every((k) => Object.hasOwn(choices, k));
}

/** The key of the first guide step (on-page order) the user hasn't explicitly
 *  decided yet, or null when every reviewable field has been reviewed. The bar
 *  blocks Save until this is null and activates this step so the user is taken
 *  to the next field awaiting a decision. */
export function firstUnreviewedKey(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
): string | null {
  return guideSteps(preview).find((s) => !stepDecided(s, choices))?.key ?? null;
}

/** Whether a still-UNREVIEWED step exists before / after the current guide step
 *  in on-page order — i.e. whether the bar's Previous / Next has somewhere to
 *  go. Both false once nothing is left to review (matches stepBy's reach, so the
 *  buttons disable exactly when they'd no-op). */
export function unreviewedNeighbors(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  guideKey: string | null,
): { prev: boolean; next: boolean } {
  const steps = guideSteps(preview);
  const index = steps.findIndex((s) => s.key === guideKey);
  let prev = false;
  let next = false;
  steps.forEach((step, i) => {
    if (stepDecided(step, choices)) return;
    if (i < index) prev = true;
    else if (i > index) next = true;
  });
  return { prev, next };
}

/** Reviewed/total step counts for the bar's progress line. */
export function guideProgress(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
): { done: number; total: number } {
  const steps = guideSteps(preview);
  return { done: steps.filter((s) => stepDecided(s, choices)).length, total: steps.length };
}

/** The active step's place in the guided walk, counting only what is still LEFT
 *  TO REVIEW: `current` is its position among the open steps, `total` how many
 *  are open. So navigation (the bar's Previous/Next) moves `current`, and every
 *  decision drops `total` by one — the counter counts down WITH the bar's
 *  "N fields left to review" instead of climbing against it (spark#198: a
 *  29-change review used to end on "29 of 29" beside "1 field left to review",
 *  and a decision on a non-active card left the counter byte-identical).
 *
 *  The ACTIVE step is always counted, even once it is itself decided, so that a
 *  card keeps its readout while the user is working IN it: editing a value calls
 *  setChoice (ProfileMergeField/-Collection) — which reviews the field without
 *  advancing the guide — so dropping a decided active step would yank the
 *  counter out from under someone mid-keystroke. Same for a card re-opened via
 *  its Review button. The cost is that this ONE state reads one higher than the
 *  bar's "N fields left to review" (it counts the re-opened card, the bar does
 *  not); it resolves as soon as the guide moves on. Everywhere the walk actually
 *  is — an undecided active step — total EQUALS the bar's open count.
 *
 *  null when no step is active, or when the active key has no step at all (a
 *  person_detail key outside DETAIL_KEY_ORDER — the card then shows no counter). */
export function guideCounter(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  guideKey: string | null,
): { current: number; total: number } | null {
  const steps = guideSteps(preview);
  if (!steps.some((s) => s.key === guideKey)) return null;
  const open = steps.filter((s) => s.key === guideKey || !stepDecided(s, choices));
  return { current: open.findIndex((s) => s.key === guideKey) + 1, total: open.length };
}

/** The choice a step currently resolves to, defaults included — what the
 *  decision buttons show as selected. null when the step's keys disagree (a
 *  mixed skills group after a partial bulk action). */
export function stepEffectiveChoice(
  preview: PreviewData | null,
  step: GuideStep,
  choices: Record<string, ResolutionChoice>,
): ResolutionChoice | null {
  const actions = new Map(actionableItems(preview).map((i) => [i.key, i.action]));
  let result: ResolutionChoice | null = null;
  for (const key of step.keys) {
    const choice = choices[key] ?? defaultChoice(actions.get(key) ?? step.action);
    if (result === null) result = choice;
    else if (result !== choice) return null;
  }
  return result;
}

// Carries the genesis LinkedIn merge **preview** from the import (Documents/
// Service page) to the in-line merge review on the profile page. The preview is
// a STATELESS diff (genesis keeps no server-side snapshot/handle since the
// pivot). In-memory only: a page refresh drops it and the user re-imports (same
// rationale as the profile-extraction proposals store). Not Apollo cache —
// these aren't genesis entities until saved — and not persisted (a stale diff
// would be misleading).
//
// Two edit surfaces feed the commit: `edits` holds per-side in-place edits of a
// person_detail compared pair (keyed by FieldDiff.key then side); `rowEdits`
// holds per-field edits of a collection row (keyed by rowKey then field). Both
// carry the machine value the commit sends.

interface ProfileMergeState {
  preview: PreviewData | null;
  // Per-field resolution overrides, keyed by FieldDiff.key / RowDiff.rowKey.
  // Absent = the item's default (ADD → USE_INCOMING, CONFLICT → KEEP_EXISTING).
  choices: Record<string, ResolutionChoice>;
  // In-place edits of the person_detail compared pair, keyed by FieldDiff.key
  // then side. Absent = the pristine snapshot value.
  edits: Record<string, Partial<Record<MergeSide, string>>>;
  // In-place edits of a collection row's INCOMING side, keyed by rowKey then
  // field. Committed when the row is taken from LinkedIn; absent = the pristine
  // incoming machine value.
  rowEdits: Record<string, Record<string, string>>;
  // In-place edits of a collection row's EXISTING (saved) side, keyed by rowKey
  // then field — the row analogue of person_detail's editable existing side.
  // Committed as a partial update of the saved entry when the row is KEPT; absent
  // = the saved value stands unchanged.
  rowExistingEdits: Record<string, Record<string, string>>;
  // The guided review's active step (GuideStep.key), or null when every step
  // is reviewed (or there is no preview). Starts on the first step.
  guideKey: string | null;
  // Bumped on every programmatic guide move (decide-advance, back/next) — the
  // active card watches it to scroll into view and take focus. Activating a
  // card by clicking into it does NOT bump (the card is already under the
  // pointer; scrolling it would yank the page).
  guideNonce: number;
  // The last guideNonce an active card has responded to (scroll + focus). Kept
  // in the store, not per card: a move can unmount its destination's whole
  // subtree (the Details personal/company tab switch), so the freshly mounted
  // card must still see the move as pending and take focus.
  guideNonceHandled: number;
  // Set when a Save was blocked by an invalid date, so every date field reveals
  // its warning — even ones the user never focused (an unedited bad import). A
  // field also shows its warning once it has been blurred; this is the global
  // "show them all now" trigger.
  revealDateErrors: boolean;
  /** Begin an in-line review with a fresh preview (clears prior choices/edits).
   *  The preview passes through `uniquifyRowKeys` first: every card, choice and
   *  edit is addressed by key, so two rows sharing a rowKey would alias into one
   *  another (see profile-merge-row-keys). This is the ONE entry point for a
   *  preview — the LinkedIn dialog and the CV extractor both land here — so no
   *  import source can bypass the guard. */
  start: (preview: PreviewData) => void;
  /** Mark the current guideNonce as responded to (the active card's effect). */
  markGuideNavHandled: () => void;
  /** Reveal every invalid-date warning at once (the bar calls this when a Save
   *  is blocked by an invalid date). */
  revealDateErrorsNow: () => void;
  /** Record the user's keep/use choice for one item. */
  setChoice: (key: string, choice: ResolutionChoice) => void;
  /** Make this step the guide's active one (clicking/focusing its card). */
  activateStep: (key: string) => void;
  /** Move to the nearest still-unreviewed step in `delta`'s direction (the bar's
   *  Previous/Next), skipping already-decided steps; no-op when none remain. */
  stepBy: (delta: number) => void;
  /** Resolve the active step's keys to `choice` and advance the guide to the
   *  next unreviewed step (wrapping); stays put when none is left. */
  decide: (stepKey: string, choice: ResolutionChoice) => void;
  /** Set the same choice for many keys at once (the global keep-all / use-all). */
  setMany: (keys: string[], choice: ResolutionChoice) => void;
  /** Record an in-place edit of one side of a person_detail field's pair. */
  setEdit: (key: string, side: MergeSide, value: string) => void;
  /** Drop the edit of one side, reverting it to the pristine snapshot value. */
  clearEdit: (key: string, side: MergeSide) => void;
  /** Record an in-place edit of one field of a collection row's INCOMING side. */
  setRowEdit: (rowKey: string, field: string, value: string) => void;
  /** Drop the incoming edit of one row field, reverting it to the pristine value. */
  clearRowEdit: (rowKey: string, field: string) => void;
  /** Record an in-place edit of one field of a collection row's EXISTING side. */
  setRowExistingEdit: (rowKey: string, field: string, value: string) => void;
  /** Drop the existing-side edit of one row field, reverting it to the saved value. */
  clearRowExistingEdit: (rowKey: string, field: string) => void;
  /** Remove already-saved items (person_detail keys and/or collection rowKeys)
   *  from the preview + their choices/edits, then re-anchor the guide. Used
   *  after a PARTIAL save so a retry re-sends only the failures and never
   *  re-creates a row that already persisted. */
  dropResolved: (keys: string[]) => void;
  /** End the review, dropping the preview, all choices, and all edits. */
  reset: () => void;
}

export const useProfileMergeStore = create<ProfileMergeState>((set) => ({
  preview: null,
  choices: {},
  edits: {},
  rowEdits: {},
  rowExistingEdits: {},
  guideKey: null,
  guideNonce: 0,
  guideNonceHandled: 0,
  revealDateErrors: false,
  start: (incoming) => {
    // SKILLS_STEP_KEY is a step key like any other, so a rowKey landing on it
    // would give guideSteps two steps under one key — the same aliasing again.
    const preview = uniquifyRowKeys(incoming, [SKILLS_STEP_KEY]);
    set({
      preview,
      choices: {},
      edits: {},
      rowEdits: {},
      rowExistingEdits: {},
      guideKey: guideSteps(preview)[0]?.key ?? null,
      guideNonce: 0,
      guideNonceHandled: 0,
      revealDateErrors: false,
    });
  },
  markGuideNavHandled: () => set((s) => ({ guideNonceHandled: s.guideNonce })),
  revealDateErrorsNow: () => set({ revealDateErrors: true }),
  setChoice: (key, choice) => set((s) => ({ choices: { ...s.choices, [key]: choice } })),
  activateStep: (key) => set((s) => (s.guideKey === key ? {} : { guideKey: key })),
  stepBy: (delta) =>
    set((s) => {
      const steps = guideSteps(s.preview);
      if (steps.length === 0) return {};
      const dir = delta >= 0 ? 1 : -1;
      const index = s.guideKey ? steps.findIndex((st) => st.key === s.guideKey) : dir > 0 ? -1 : steps.length;
      // Prev/Next visit only the steps still LEFT TO REVIEW, skipping ones the
      // user already decided; no move when none remain in that direction (an
      // already-decided card is revisited via its Review button, not the nav).
      for (let i = index + dir; i >= 0 && i < steps.length; i += dir) {
        if (!stepDecided(steps[i]!, s.choices)) {
          return { guideKey: steps[i]!.key, guideNonce: s.guideNonce + 1 };
        }
      }
      return {};
    }),
  decide: (stepKey, choice) =>
    set((s) => {
      const steps = guideSteps(s.preview);
      const index = steps.findIndex((st) => st.key === stepKey);
      // Unknown step (defensive): record the bare key, don't move the guide.
      if (index < 0) return { choices: { ...s.choices, [stepKey]: choice } };
      const choices = { ...s.choices };
      for (const key of steps[index]!.keys) choices[key] = choice;
      // Advance to the next unreviewed step after this one, wrapping.
      for (let i = 1; i < steps.length; i++) {
        const candidate = steps[(index + i) % steps.length]!;
        if (!stepDecided(candidate, choices)) {
          return { choices, guideKey: candidate.key, guideNonce: s.guideNonce + 1 };
        }
      }
      // Deciding the LAST open field leaves nothing to review: no card stays
      // active, so the just-decided card collapses to its status + Review line
      // like every other reviewed field.
      return { choices, guideKey: null };
    }),
  setMany: (keys, choice) =>
    set((s) => {
      const choices = { ...s.choices };
      for (const k of keys) choices[k] = choice;
      // A bulk keep-all / use-all reviews everything at once → collapse the
      // active card too (nothing left to review). If some step is still open,
      // the active card stays put.
      const anyOpen = guideSteps(s.preview).some((step) => !stepDecided(step, choices));
      return anyOpen ? { choices } : { choices, guideKey: null };
    }),
  setEdit: (key, side, value) =>
    set((s) => ({ edits: { ...s.edits, [key]: { ...s.edits[key], [side]: value } } })),
  clearEdit: (key, side) =>
    set((s) => {
      const forKey = { ...s.edits[key] };
      delete forKey[side];
      const next = { ...s.edits };
      if (Object.keys(forKey).length === 0) delete next[key];
      else next[key] = forKey;
      return { edits: next };
    }),
  setRowEdit: (rowKey, field, value) =>
    set((s) => ({ rowEdits: { ...s.rowEdits, [rowKey]: { ...s.rowEdits[rowKey], [field]: value } } })),
  clearRowEdit: (rowKey, field) =>
    set((s) => {
      const forRow = { ...s.rowEdits[rowKey] };
      delete forRow[field];
      const next = { ...s.rowEdits };
      if (Object.keys(forRow).length === 0) delete next[rowKey];
      else next[rowKey] = forRow;
      return { rowEdits: next };
    }),
  setRowExistingEdit: (rowKey, field, value) =>
    set((s) => ({
      rowExistingEdits: {
        ...s.rowExistingEdits,
        [rowKey]: { ...s.rowExistingEdits[rowKey], [field]: value },
      },
    })),
  clearRowExistingEdit: (rowKey, field) =>
    set((s) => {
      const forRow = { ...s.rowExistingEdits[rowKey] };
      delete forRow[field];
      const next = { ...s.rowExistingEdits };
      if (Object.keys(forRow).length === 0) delete next[rowKey];
      else next[rowKey] = forRow;
      return { rowExistingEdits: next };
    }),
  dropResolved: (keys) =>
    set((s) => {
      if (!s.preview) return {};
      const drop = new Set(keys);
      const keepRows = (rows: RowDiff[]) => rows.filter((r) => !drop.has(r.rowKey));
      const preview: PreviewData = {
        // Rebuilt field-by-field, so carry the provenance tag over explicitly —
        // dropping it would silently re-source the surviving rows as LinkedIn.
        source: s.preview.source,
        personDetail: s.preview.personDetail.filter((d) => !drop.has(d.key)),
        experiences: keepRows(s.preview.experiences),
        educations: keepRows(s.preview.educations),
        languageSkills: keepRows(s.preview.languageSkills),
        certificates: keepRows(s.preview.certificates),
        expertises: keepRows(s.preview.expertises),
      };
      const prune = <V>(map: Record<string, V>): Record<string, V> => {
        const next = { ...map };
        for (const k of keys) delete next[k];
        return next;
      };
      const choices = prune(s.choices);
      const steps = guideSteps(preview);
      return {
        preview,
        choices,
        edits: prune(s.edits),
        rowEdits: prune(s.rowEdits),
        rowExistingEdits: prune(s.rowExistingEdits),
        // Anchor on the first step still awaiting a decision — NOT on steps[0],
        // which after a partial save is a row the user already decided (only the
        // SAVED keys are pruned, so every survivor keeps its choice). Re-opening
        // a decided card there gave it live buttons and a counter while the bar
        // read "All changes reviewed". Nothing open → no active card, and the
        // retry Save proceeds straight to the commit.
        guideKey: steps.find((step) => !stepDecided(step, choices))?.key ?? null,
        guideNonce: s.guideNonce + 1,
        guideNonceHandled: s.guideNonce + 1,
      };
    }),
  reset: () =>
    set({
      preview: null,
      choices: {},
      edits: {},
      rowEdits: {},
      rowExistingEdits: {},
      guideKey: null,
      guideNonce: 0,
      guideNonceHandled: 0,
      revealDateErrors: false,
    }),
}));

/** Every still-actionable (non-UNCHANGED) item across person_detail and all five
 *  collections, as {key, action}. Empty when there is no preview or nothing to
 *  review. Drives the global keep-all / use-all bar and the change count. */
export function actionableItems(preview: PreviewData | null): { key: string; action: DiffAction }[] {
  if (!preview) return [];
  const out: { key: string; action: DiffAction }[] = [];
  for (const f of preview.personDetail) {
    if (f.action !== "UNCHANGED") out.push({ key: f.key, action: f.action });
  }
  for (const section of COLLECTION_KEYS) {
    for (const r of preview[section]) {
      if (r.action !== "UNCHANGED") out.push({ key: r.rowKey, action: r.action });
    }
  }
  return out;
}

/** The default resolution for an item the user hasn't touched. Mirrors genesis:
 *  a new value is taken, a conflicting value keeps the user's existing one. */
export function defaultChoice(action: DiffAction): ResolutionChoice {
  return action === "CONFLICT" ? "KEEP_EXISTING" : "USE_INCOMING";
}

/** True when a collection section has at least one actionable (non-UNCHANGED)
 *  imported row — used so the profile page renders that section's box (and its
 *  in-line merge rows) even when the user currently has no entries there. */
export function sectionHasActionable(preview: PreviewData | null, section: CollectionKey): boolean {
  return !!preview && preview[section].some((r) => r.action !== "UNCHANGED");
}

/** The still-actionable person_detail diff for a genesis field key (bio,
 *  profession, currentEmployer, …), or null when there is nothing to review for
 *  it (no preview, no matching field, or the field is UNCHANGED). */
export function detailDiffFor(preview: PreviewData | null, fieldKey: string): FieldDiff | null {
  if (!preview) return null;
  const diff = preview.personDetail.find((d) => d.key === fieldKey);
  if (!diff || diff.action === "UNCHANGED") return null;
  return diff;
}

/** The untouched snapshot value of one side of a person_detail compared pair. */
export function pristineValue(diff: FieldDiff, side: MergeSide): string | null {
  return side === "existing" ? diff.existingValue : diff.incomingValue;
}

/** The untouched incoming machine value of one collection-row field (the value
 *  an incoming-side edit is measured against), or "" when the field carries none. */
export function rowFieldPristine(row: RowDiff, field: string): string {
  return row.fields.find((f) => f.field === field)?.incomingValue ?? "";
}

/** The untouched EXISTING (saved) machine value of one collection-row field (the
 *  value an existing-side edit is measured against), or "" when none. */
export function rowFieldExistingPristine(row: RowDiff, field: string): string {
  return row.fields.find((f) => f.field === field)?.existingValue ?? "";
}

/** True when committing the CURRENT choices/edits would overwrite this field's
 *  non-empty saved value — "Use LinkedIn" over an existing value, or an
 *  in-place edit of the existing side. The saved value may come from anywhere
 *  (typed, document import, admin), so the review flags this destructive
 *  direction with neutral warning text before it is saved. */
export function replacesSavedValue(
  diff: FieldDiff,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
): boolean {
  // Additive fields (citizenship) append rather than overwrite — the incoming
  // value is a superset of the saved one, so nothing is lost.
  if (diff.additive) return false;
  if (!diff.existingValue) return false;
  const choice = choices[diff.key] ?? defaultChoice(diff.action);
  if (choice === "USE_INCOMING") return true;
  const edited = edits[diff.key]?.existing?.trim();
  return edited !== undefined && edited !== "" && edited !== diff.existingValue;
}

/** True when committing the current choice for this collection row would overwrite
 *  the matched saved entry — a CONFLICT row (one that matched an existing entry)
 *  whose effective choice is USE_INCOMING. Editing a field flips the row to
 *  USE_INCOMING, so an edited row shows the note; an explicit keep (which discards
 *  the edit at commit) hides it. ADD rows have no saved entry to lose. */
export function rowReplacesSaved(
  row: RowDiff,
  choices: Record<string, ResolutionChoice>,
): boolean {
  if (row.action !== "CONFLICT" || row.existingRowId === null) return false;
  return (choices[row.rowKey] ?? defaultChoice(row.action)) === "USE_INCOMING";
}

// The commit resolution for one person_detail field: carries `overrideValue`
// only when the CHOSEN side was edited to a non-empty value that differs from
// its snapshot (an edit of the discarded side is dropped; a blank edit falls
// back to the pristine value, since a blank override would be a destructive
// clear the preview never offers).
function detailResolution(
  diff: FieldDiff,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
): ImportResolution {
  const choice = choices[diff.key] ?? defaultChoice(diff.action);
  const side: MergeSide = choice === "KEEP_EXISTING" ? "existing" : "incoming";
  const edited = edits[diff.key]?.[side]?.trim();
  if (edited === undefined || edited === "" || edited === pristineValue(diff, side)) {
    return { key: diff.key, choice };
  }
  return { key: diff.key, choice, overrideValue: edited };
}

// The per-field overrides for one collection row's chosen side: for each
// EDITABLE field the user changed away from its pristine value (`pristineOf`
// selects the incoming or existing snapshot), `{ field, value }`. endDate is
// compared raw (blank = ongoing/"present" is a legitimate value); every other
// field is trimmed and a blank edit is dropped. Edits of non-editable fields
// (a resolved language tag, a skill name) never leak — the sole exception is a
// language the import could not resolve, which the user must be able to correct
// (see languageNeedsChoice).
/** Whether a language row field carries a proposal genesis could not resolve to a
 *  tag, so the user has to pick the real language (or skip the entry). genesis
 *  resolves a language NAME before diffing — "Türkçe"/"İngilizce"/"Englisch"
 *  arrive as tr/en and match the saved row — so a value left unresolved is
 *  something it did not recognise as a language at all ("mylang kuruhasan").
 *
 *  It marks a language tag non-editable, yet this is the one value that MUST be
 *  correctable: the review renders a chooser for it, the commit accepts the
 *  chosen tag as an override, and firstUnknownLanguageKey blocks a save that
 *  would still write the unresolved text. A row proposing no new tag (only a
 *  level change) is untouched — there is nothing to correct. */
export function languageNeedsChoice(spec: RowDiff["fields"][number]): boolean {
  if (spec.field !== "language") return false;
  const proposed = spec.incomingValue?.trim() ?? "";
  return proposed !== "" && !isKnownLanguageTag(proposed);
}

/** Per-row form of firstUnknownLanguageKey: taking this row would still write an
 *  unresolved language, so the card's Add/Use button stays disabled until the
 *  chooser holds a real tag. */
export function rowTakeBlockedByUnknownLanguage(
  row: RowDiff,
  rowEdits: Record<string, Record<string, string>>,
): boolean {
  const spec = row.fields.find((f) => f.field === "language");
  if (!spec || !languageNeedsChoice(spec)) return false;
  /* v8 ignore next -- unreachable: languageNeedsChoice just proved incomingValue is non-blank, so its `?? ""` arm can never be taken */
  const tag = (rowEdits[row.rowKey]?.language ?? spec.incomingValue ?? "").trim();
  return tag !== "" && !isKnownLanguageTag(tag);
}

function rowFieldOverrides(
  row: RowDiff,
  edited: Record<string, string>,
  pristineOf: (spec: RowDiff["fields"][number]) => string,
): ImportFieldOverride[] {
  const out: ImportFieldOverride[] = [];
  for (const spec of row.fields) {
    // A corrected language is committed even though genesis marked it non-editable —
    // otherwise the pick is recorded in the store and silently dropped at commit,
    // creating the row with the unresolved text after all.
    if (!spec.editable && !languageNeedsChoice(spec)) continue;
    const raw = edited[spec.field];
    if (raw === undefined) continue;
    const pristine = pristineOf(spec);
    if (spec.field === "endDate") {
      if (raw !== pristine) out.push({ field: spec.field, value: raw });
    } else {
      const trimmed = raw.trim();
      if (trimmed !== "" && trimmed !== pristine) out.push({ field: spec.field, value: trimmed });
    }
  }
  return out;
}

// The commit resolution for one collection row. Editing an INCOMING field flips
// the row to USE_INCOMING (the in-line UI does this on the first edit), and such
// a row is committed with its per-field overrides. A KEPT row (the per-row "Keep
// mine" toggle, the bar's "Keep all mine", or a CONFLICT default) carries
// `existingFieldOverrides` when the user edited its EXISTING side in place — a
// partial update of the saved entry; otherwise the saved entry stands untouched.
// An explicit keep wins over any stale incoming edit (which stays in the store,
// so flipping back to "Use" re-applies it).
function rowResolution(
  row: RowDiff,
  choices: Record<string, ResolutionChoice>,
  rowEdits: Record<string, Record<string, string>>,
  rowExistingEdits: Record<string, Record<string, string>>,
): ImportResolution {
  const choice = choices[row.rowKey] ?? defaultChoice(row.action);
  if (choice === "KEEP_EXISTING") {
    const existingFieldOverrides = rowFieldOverrides(
      row,
      rowExistingEdits[row.rowKey] ?? {},
      (spec) => spec.existingValue ?? "",
    );
    if (existingFieldOverrides.length === 0) return { key: row.rowKey, choice };
    return { key: row.rowKey, choice, existingFieldOverrides };
  }
  const fieldOverrides = rowFieldOverrides(row, rowEdits[row.rowKey] ?? {}, (spec) => spec.incomingValue ?? "");
  if (fieldOverrides.length === 0) return { key: row.rowKey, choice };
  return { key: row.rowKey, choice: "USE_INCOMING", fieldOverrides };
}

/** Builds the commit resolutions from the current choices and in-place edits.
 *  person_detail fields may carry `overrideValue` (the edited chosen side);
 *  collection rows may carry `fieldOverrides` (the edited per-field values).
 *  Order mirrors `actionableItems`: person_detail first, then each collection. */
export function resolutionsFor(
  preview: PreviewData,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
): ImportResolution[] {
  const out: ImportResolution[] = [];
  for (const diff of preview.personDetail) {
    if (diff.action !== "UNCHANGED") out.push(detailResolution(diff, choices, edits));
  }
  for (const section of COLLECTION_KEYS) {
    for (const row of preview[section]) {
      if (row.action !== "UNCHANGED") out.push(rowResolution(row, choices, rowEdits, rowExistingEdits));
    }
  }
  return out;
}

// The genesis-required text field a CREATE/UPDATE needs per collection — the
// same guards createRun/updateRun apply. A USE_INCOMING row whose required field
// is blank cannot be built, so the commit never sends it (or its dates); the
// date gate must skip such a row or it would falsely block the whole save on a
// date that never reaches genesis. Sections with no MONTH date are irrelevant.
const REQUIRED_ROW_FIELD: Partial<Record<CollectionKey, string>> = {
  experiences: "title",
  educations: "fieldOfStudy",
  certificates: "name",
};

// The effective, non-blank date values a save WOULD commit, each tagged with its
// guide-step key — the exact values the commit builds, so a validity gate over
// them matches what reaches genesis:
//   - person_detail dateOfBirth: the taken value (USE_INCOMING → the incoming
//     edit or the incoming value; KEEP_EXISTING → only a non-blank existing edit).
//   - collection MONTH fields (startDate/endDate/issued): for a taken row every
//     incoming date field (edit ?? incoming) — UNLESS the row's required field is
//     blank (then the row isn't committed, so its dates are skipped); for a kept
//     row only the existing date fields the user edited. Blank/ongoing is skipped.
function committedDateValues(
  preview: PreviewData,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
  rowEdits: Record<string, Record<string, string>>,
  rowExistingEdits: Record<string, Record<string, string>>,
): { key: string; value: string }[] {
  const out: { key: string; value: string }[] = [];
  const push = (key: string, v: string | null | undefined) => {
    if (v != null && v.trim() !== "") out.push({ key, value: v });
  };

  for (const diff of preview.personDetail) {
    if (diff.action === "UNCHANGED" || !DATE_DETAIL_KEYS.has(diff.key)) continue;
    const choice = choices[diff.key] ?? defaultChoice(diff.action);
    if (choice === "USE_INCOMING") {
      const edited = edits[diff.key]?.incoming?.trim();
      push(diff.key, edited && edited !== "" ? edited : diff.incomingValue);
    } else {
      // Kept: only a real in-place edit of the existing side is committed.
      push(diff.key, edits[diff.key]?.existing);
    }
  }

  for (const section of COLLECTION_KEYS) {
    for (const row of preview[section]) {
      if (row.action === "UNCHANGED") continue;
      const choice = choices[row.rowKey] ?? defaultChoice(row.action);
      // Skip a taken row the commit can't build (blank required field) — its
      // dates aren't sent, so validating them would falsely block the save.
      if (choice === "USE_INCOMING") {
        const reqField = REQUIRED_ROW_FIELD[section];
        if (reqField) {
          const spec = row.fields.find((f) => f.field === reqField);
          const edited = rowEdits[row.rowKey]?.[reqField];
          const reqValue = (edited !== undefined ? edited : spec?.incomingValue) ?? "";
          if (reqValue.trim() === "") continue;
        }
      }
      for (const spec of row.fields) {
        if (spec.kind !== "MONTH" || !spec.editable) continue;
        if (choice === "USE_INCOMING") {
          const edited = rowEdits[row.rowKey]?.[spec.field];
          push(row.rowKey, edited !== undefined ? edited : spec.incomingValue);
        } else {
          // Kept: only edited existing date fields are committed.
          push(row.rowKey, rowExistingEdits[row.rowKey]?.[spec.field]);
        }
      }
    }
  }
  return out;
}

/** True when a save right now would commit a date the review must reject — an
 *  invalid shape/calendar value, a date after `maxDate`, an inverted start/end
 *  range, or an entry date before the date of birth — so the bar can block the
 *  save and reveal the per-field warnings instead of persisting bad data. */
export function hasInvalidDates(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
  maxDate: string = todayIso(),
): boolean {
  return firstInvalidDateKey(preview, choices, edits, rowEdits, rowExistingEdits, maxDate) !== null;
}

/** The key of the first committed person_detail value that fails L2 validation (length / email /
 *  phone — mirroring the genesis L1 sanitizer), or null. Blocks the save the same way an invalid date
 *  does, so an out-of-range edit or a fail-open-kept invalid phone/email can't be committed. */
export function firstInvalidFieldKey(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
): string | null {
  if (!preview) return null;
  for (const diff of preview.personDetail) {
    if (diff.action === "UNCHANGED") continue;
    const choice = choices[diff.key] ?? defaultChoice(diff.action);
    let value: string | null | undefined;
    if (choice === "USE_INCOMING") {
      const edited = edits[diff.key]?.incoming?.trim();
      value = edited && edited !== "" ? edited : diff.incomingValue;
    } else {
      // Kept: only a real in-place edit of the existing side is committed.
      value = edits[diff.key]?.existing;
    }
    if (importFieldError(diff.key, value)) return diff.key;
  }
  return null;
}

/** The rowKey of the first collection row the commit would write with an over-cap field value, or null.
 *  The editor gained these caps first; without the same gate here the review would accept a row the
 *  editor refuses — the mirror image of the divergence this ticket was filed about. */
export function firstInvalidRowFieldKey(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
): string | null {
  if (!preview) return null;
  // Object.entries rather than keys + lookup: `caps` is then non-optional, so there is no dead
  // "section has no caps" branch to explain away.
  for (const [section, caps] of Object.entries(ROW_FIELD_MAX_CHARS)) {
    for (const row of preview[section as CollectionKey] ?? []) {
      if (row.action === "UNCHANGED") continue;
      const choice = choices[row.rowKey] ?? defaultChoice(row.action);
      for (const field of Object.keys(caps)) {
        const value = committedRowFieldValue(row, field, choice, rowEdits, rowExistingEdits);
        if (rowFieldError(section, field, value)) return row.rowKey;
      }
    }
  }
  return null;
}

export function hasInvalidFields(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
): boolean {
  return (
    firstInvalidFieldKey(preview, choices, edits) !== null ||
    firstInvalidRowFieldKey(preview, choices, rowEdits, rowExistingEdits) !== null
  );
}

/** The guide-step key (person_detail field key or collection rowKey) of the
 *  FIRST committed date that fails validation, in commit order, or null when
 *  every committed date is acceptable. The bar activates this step on a blocked
 *  save so the offending field's card (and its warning) is revealed — including
 *  a dateOfBirth on the currently-hidden Details tab. */
// Sections whose rows carry BOTH a startDate and an endDate, so the start≤end
// rule applies. certificates has a single date; language/expertise have none.
const DATE_RANGE_SECTIONS: CollectionKey[] = ["experiences", "educations"];

// The value a save WOULD commit for one field of a row, resolving edits over the
// side the current choice takes (incoming for USE_INCOMING, existing otherwise).
function committedRowFieldValue(
  row: RowDiff,
  field: string,
  choice: ResolutionChoice,
  rowEdits: Record<string, Record<string, string>>,
  rowExistingEdits: Record<string, Record<string, string>>,
): string {
  const spec = row.fields.find((f) => f.field === field);
  if (!spec) return "";
  if (choice === "USE_INCOMING") {
    const edited = rowEdits[row.rowKey]?.[field];
    return (edited !== undefined ? edited : spec.incomingValue) ?? "";
  }
  const edited = rowExistingEdits[row.rowKey]?.[field];
  return (edited !== undefined ? edited : spec.existingValue) ?? "";
}

/** The rowKey of the first language row the commit would write with a value that
 *  is not a real language, or null.
 *
 *  genesis resolves a language NAME to its tag before diffing, so "Türkçe" /
 *  "İngilizce" / "Englisch" arrive as `tr` / `en` and match the saved row. What
 *  it could not resolve arrives verbatim ("mylang kuruhasan"). Since languageTag
 *  is free text genesis stores as-is, taking such a row over would persist
 *  permanent junk that no language filter ever matches — so the review keeps the
 *  row, flags it, and blocks the save until the user picks a real language or
 *  skips the entry. Only a row being TAKEN is judged: keeping/skipping writes no
 *  tag. */
export function firstUnknownLanguageKey(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
): string | null {
  if (!preview) return null;
  for (const row of preview.languageSkills ?? []) {
    if (row.action === "UNCHANGED") continue;
    const choice = choices[row.rowKey] ?? defaultChoice(row.action);
    if (choice !== "USE_INCOMING") continue;
    const tag = committedRowFieldValue(row, "language", choice, rowEdits, rowExistingEdits);
    // An empty tag writes nothing (profile-merge-commit skips the row), so it is
    // not an unknown language — only a non-empty unresolved value blocks.
    if (tag.trim() !== "" && !isKnownLanguageTag(tag)) return row.rowKey;
  }
  return null;
}

export function hasUnknownLanguages(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
): boolean {
  return firstUnknownLanguageKey(preview, choices, rowEdits, rowExistingEdits) !== null;
}

/** The rowKey of the first committed experience/education row whose start date
 *  falls after its end date, or null. Skips a USE_INCOMING row the commit can't
 *  build (blank required field) — same guard as committedDateValues — so a bad
 *  range on a row that never reaches genesis doesn't falsely block the save. */
export function firstStartAfterEndKey(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
): string | null {
  if (!preview) return null;
  for (const section of DATE_RANGE_SECTIONS) {
    for (const row of preview[section] ?? []) {
      if (row.action === "UNCHANGED") continue;
      const choice = choices[row.rowKey] ?? defaultChoice(row.action);
      if (choice === "USE_INCOMING") {
        const reqField = REQUIRED_ROW_FIELD[section];
        /* v8 ignore next -- unreachable: DATE_RANGE_SECTIONS is experiences+educations and REQUIRED_ROW_FIELD defines a field for both, so reqField is always set */
        if (reqField) {
          const spec = row.fields.find((f) => f.field === reqField);
          const edited = rowEdits[row.rowKey]?.[reqField];
          const reqValue = (edited !== undefined ? edited : spec?.incomingValue) ?? "";
          if (reqValue.trim() === "") continue;
        }
      }
      const start = committedRowFieldValue(row, "startDate", choice, rowEdits, rowExistingEdits);
      const end = committedRowFieldValue(row, "endDate", choice, rowEdits, rowExistingEdits);
      if (isStartAfterEnd(start, end)) return row.rowKey;
    }
  }
  return null;
}

/** The date of birth the commit would leave on the profile: the taken side of an
 *  imported dateOfBirth diff (edit ?? value), the edited/saved existing on a kept
 *  one, or the saved value when it is UNCHANGED. Null when the profile has no DOB
 *  in the preview (then no birth-coherence check can run). */
export function committedDateOfBirth(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
): string | null {
  if (!preview) return null;
  const diff = preview.personDetail.find((d) => d.key === "dateOfBirth");
  if (!diff) return null;
  if (diff.action === "UNCHANGED") return diff.existingValue ?? diff.incomingValue ?? null;
  const choice = choices[diff.key] ?? defaultChoice(diff.action);
  if (choice === "USE_INCOMING") {
    const edited = edits[diff.key]?.incoming?.trim();
    return (edited && edited !== "" ? edited : diff.incomingValue) ?? null;
  }
  const edited = edits[diff.key]?.existing;
  return (edited != null && edited.trim() !== "" ? edited : diff.existingValue) ?? null;
}

// The one date per collection row that must not precede birth: the experience/
// education START and the certificate ISSUE date (an end date follows from start≤end).
const BIRTH_ANCHOR_FIELD: Partial<Record<CollectionKey, string>> = {
  experiences: "startDate",
  educations: "startDate",
  certificates: "issued",
};

/** The rowKey of the first committed experience/education/certificate whose anchor
 *  date falls before the committed date of birth, or null. Skips a USE_INCOMING row
 *  the commit can't build (blank required field), same guard as the other gates. */
export function firstDateBeforeBirthKey(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
): string | null {
  if (!preview) return null;
  const dob = committedDateOfBirth(preview, choices, edits);
  if (!dob) return null;
  for (const section of COLLECTION_KEYS) {
    const anchor = BIRTH_ANCHOR_FIELD[section];
    if (!anchor) continue;
    for (const row of preview[section] ?? []) {
      if (row.action === "UNCHANGED") continue;
      const choice = choices[row.rowKey] ?? defaultChoice(row.action);
      if (choice === "USE_INCOMING") {
        const reqField = REQUIRED_ROW_FIELD[section];
        /* v8 ignore next -- unreachable: every BIRTH_ANCHOR_FIELD section (experiences/educations/certificates) also has a REQUIRED_ROW_FIELD, so reqField is always set */
        if (reqField) {
          const spec = row.fields.find((f) => f.field === reqField);
          const edited = rowEdits[row.rowKey]?.[reqField];
          const reqValue = (edited !== undefined ? edited : spec?.incomingValue) ?? "";
          if (reqValue.trim() === "") continue;
        }
      }
      const date = committedRowFieldValue(row, anchor, choice, rowEdits, rowExistingEdits);
      if (isBeforeBirthDate(date, dob)) return row.rowKey;
    }
  }
  return null;
}

export function firstInvalidDateKey(
  preview: PreviewData | null,
  choices: Record<string, ResolutionChoice>,
  edits: Record<string, Partial<Record<MergeSide, string>>>,
  rowEdits: Record<string, Record<string, string>> = {},
  rowExistingEdits: Record<string, Record<string, string>> = {},
  maxDate: string = todayIso(),
): string | null {
  if (!preview) return null;
  const bad = committedDateValues(preview, choices, edits, rowEdits, rowExistingEdits).find(
    (e) => dateFieldError(e.value, maxDate) !== null,
  );
  if (bad) return bad.key;
  // A well-formed but inverted start/end range also blocks the save.
  const inverted = firstStartAfterEndKey(preview, choices, rowEdits, rowExistingEdits);
  if (inverted) return inverted;
  // …as does an entry (experience/education start, certificate issue) dated before birth.
  return firstDateBeforeBirthKey(preview, choices, edits, rowEdits, rowExistingEdits);
}
